import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  IconButton,
  AppBar,
  Toolbar,
  Container,
  CircularProgress,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  ArrowBack,
  Delete,
  Visibility,
  VisibilityOff,
  Person,
  AccessTime,
  ThumbUp,
  Comment,
  Flag,
  CheckCircle
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface Post {
  id: string;
  userId: string;
  username: string;
  userPhotoURL: string;
  text: string;
  imageURL?: string;
  createdAt: any;
  updatedAt: any;
  likeCount: number;
  commentCount: number;
  isHidden: boolean;
  reportCount: number;
}

interface ContentReport {
  id: string;
  contentId: string;
  contentType: string;
  reason: string;
  description?: string;
  reporterUserId: string;
  reporterUsername: string;
  reporterPhotoURL: string;
  reportedUserId: string;
  reportedUsername: string;
  reportedPhotoURL: string;
  createdAt: any;
  status: 'pending' | 'reviewed' | 'dismissed';
  reviewedBy?: string;
}

const PostsManagementPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [posts, setPosts] = useState<Post[]>([]);
  const [reports, setReports] = useState<ContentReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPosts, setTotalPosts] = useState(0);
  const [totalReports, setTotalReports] = useState(0);
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [selectedReport, setSelectedReport] = useState<ContentReport | null>(null);
  const [reportStatus, setReportStatus] = useState<'reviewed' | 'dismissed'>('reviewed');
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const PRIMARY_COLOR = '#8B4513';
  const SECONDARY_COLOR = '#D2691E';

  useEffect(() => {
    loadPosts();
    loadReports();
  }, [page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPosts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await axios.get(`${API_BASE_URL}/api/posts`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          limit: rowsPerPage,
          offset: page * rowsPerPage
        }
      });
      
        if (response.data.success) {
          setPosts(response.data.data.posts || []);
          setTotalPosts(response.data.data.total || 0);
        }
      
    } catch (error) {
      console.error('Error loading posts:', error);
      showSnackbar('Postlar yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await axios.get(`${API_BASE_URL}/api/content-reports`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          contentType: 'post',
          limit: rowsPerPage,
          offset: page * rowsPerPage
        }
      });
      
      if (response.data.success) {
        setReports(response.data.data.reports || []);
        setTotalReports(response.data.data.total || 0);
      }
      
    } catch (error) {
      console.error('Error loading reports:', error);
      showSnackbar('Raporlar yüklenirken hata oluştu', 'error');
    }
  };

  const handleDeletePost = async () => {
    if (!selectedPost) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_BASE_URL}/api/posts/${selectedPost.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setPosts(posts.filter((post: Post) => post.id !== selectedPost.id));
      setDeleteDialogOpen(false);
      setSelectedPost(null);
      showSnackbar('Post başarıyla silindi', 'success');
    } catch (error) {
      console.error('Error deleting post:', error);
      showSnackbar('Post silinirken hata oluştu', 'error');
    }
  };

  const handleTogglePostVisibility = async (post: Post) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API_BASE_URL}/api/posts/${post.id}/visibility`, {
        isHidden: !post.isHidden
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setPosts(posts.map((p: Post) => 
        p.id === post.id ? { ...p, isHidden: !p.isHidden } : p
      ));
      
      showSnackbar(
        post.isHidden ? 'Post görünür hale getirildi' : 'Post gizlendi', 
        'success'
      );
    } catch (error) {
      console.error('Error toggling post visibility:', error);
      showSnackbar('Post durumu değiştirilirken hata oluştu', 'error');
    }
  };

  const handleReviewReport = async () => {
    if (!selectedReport) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API_BASE_URL}/api/content-reports/${selectedReport.id}/status`, {
        status: reportStatus
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setReports(reports.map((r: ContentReport) => 
        r.id === selectedReport.id ? { ...r, status: reportStatus } : r
      ));
      
      setReportDialogOpen(false);
      setSelectedReport(null);
      showSnackbar('Rapor durumu güncellendi', 'success');
    } catch (error) {
      console.error('Error reviewing report:', error);
      showSnackbar('Rapor durumu güncellenirken hata oluştu', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Tarih yok';
    
    let date;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleString('tr-TR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'reviewed': return 'success';
      case 'dismissed': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Beklemede';
      case 'reviewed': return 'İncelendi';
      case 'dismissed': return 'Reddedildi';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={80} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6" color="white">
            Yükleniyor...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
      {/* App Bar */}
      <AppBar
        position="static"
        sx={{
          background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          
          <Typography variant="h5" component="div" sx={{ 
            fontWeight: 700,
            color: 'white',
            flexGrow: 1
          }}>
            İçerik Yönetimi
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Stats Cards */}
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 3, 
          mb: 4 
        }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
              color: 'white'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {totalPosts}
                </Typography>
                <Typography variant="body1">
                  Toplam Post
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${SECONDARY_COLOR} 0%, #CD853F 100%)`,
              color: 'white'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {reports.filter(r => r.status === 'pending').length}
                </Typography>
                <Typography variant="body1">
                  Bekleyen Rapor
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card sx={{ 
              background: `linear-gradient(135deg, #CD853F 0%, #DEB887 100%)`,
              color: 'white'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {posts.filter(p => p.isHidden).length}
                </Typography>
                <Typography variant="body1">
                  Gizli Post
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card sx={{ 
              background: `linear-gradient(135deg, #DEB887 0%, #F4A460 100%)`,
              color: 'white'
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {totalReports}
                </Typography>
                <Typography variant="body1">
                  Toplam Rapor
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={(e: any, newValue: any) => setTabValue(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              label={`Postlar (${totalPosts})`} 
              icon={<Comment />} 
              iconPosition="start"
            />
            <Tab 
              label={`Raporlar (${totalReports})`} 
              icon={<Flag />} 
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* Posts Tab */}
        {tabValue === 0 && (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Kullanıcı</TableCell>
                    <TableCell>İçerik</TableCell>
                    <TableCell>Tarih</TableCell>
                    <TableCell>Beğeni</TableCell>
                    <TableCell>Rapor</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {posts.map((post: Post) => (
                    <TableRow key={post.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar 
                            src={post.userPhotoURL} 
                            sx={{ width: 32, height: 32 }}
                          >
                            <Person />
                          </Avatar>
                          <Typography variant="body2">
                            {post.username}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {post.text.length > 100 
                            ? `${post.text.substring(0, 100)}...` 
                            : post.text
                          }
                        </Typography>
                        {post.imageURL && (
                          <Chip 
                            label="Resim" 
                            size="small" 
                            color="primary" 
                            sx={{ mt: 1, cursor: 'pointer' }}
                            onClick={() => window.open(post.imageURL, '_blank')}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTime sx={{ fontSize: 16 }} />
                          <Typography variant="body2">
                            {formatDate(post.createdAt)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ThumbUp sx={{ fontSize: 16 }} />
                          <Typography variant="body2">
                            {post.likeCount}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={post.reportCount} 
                          color={post.reportCount > 0 ? 'error' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={post.isHidden ? 'Gizli' : 'Görünür'}
                          color={post.isHidden ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleTogglePostVisibility(post)}
                            color={post.isHidden ? 'success' : 'warning'}
                          >
                            {post.isHidden ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedPost(post);
                              setDeleteDialogOpen(true);
                            }}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalPosts}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e: any, newPage: any) => setPage(newPage)}
              onRowsPerPageChange={(e: any) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        )}

        {/* Reports Tab */}
        {tabValue === 1 && (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Rapor Eden</TableCell>
                    <TableCell>Rapor Edilen</TableCell>
                    <TableCell>Sebep</TableCell>
                    <TableCell>Tarih</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report: ContentReport) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar 
                            src={report.reporterPhotoURL} 
                            sx={{ width: 32, height: 32 }}
                          >
                            <Person />
                          </Avatar>
                          <Typography variant="body2">
                            {report.reporterUsername}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar 
                            src={report.reportedPhotoURL} 
                            sx={{ width: 32, height: 32 }}
                          >
                            <Person />
                          </Avatar>
                          <Typography variant="body2">
                            {report.reportedUsername}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={report.reason}
                          color="warning"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTime sx={{ fontSize: 16 }} />
                          <Typography variant="body2">
                            {formatDate(report.createdAt)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusText(report.status)}
                          color={getStatusColor(report.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {report.status === 'pending' && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedReport(report);
                                setReportDialogOpen(true);
                              }}
                              color="primary"
                            >
                              <CheckCircle />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => {
                              // Post silme işlemi
                              const postToDelete = posts.find(p => p.id === report.contentId);
                              if (postToDelete) {
                                setSelectedPost(postToDelete);
                                setDeleteDialogOpen(true);
                              }
                            }}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalReports}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e: any, newPage: any) => setPage(newPage)}
              onRowsPerPageChange={(e: any) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        )}
      </Container>

      {/* Delete Post Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Post Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu postu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </Typography>
          {selectedPost && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Kullanıcı:</strong> {selectedPost.username}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>İçerik:</strong> {selectedPost.text}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeletePost} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Report Dialog */}
      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)}>
        <DialogTitle>Rapor İncele</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Sebep:</strong> {selectedReport.reason}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Açıklama:</strong> {selectedReport.description || 'Açıklama yok'}
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={reportStatus}
                  label="Durum"
                  onChange={(e: any) => setReportStatus(e.target.value as 'reviewed' | 'dismissed')}
                >
                  <MenuItem value="reviewed">İncelendi</MenuItem>
                  <MenuItem value="dismissed">Reddedildi</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>İptal</Button>
          <Button onClick={handleReviewReport} color="primary" variant="contained">
            Güncelle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PostsManagementPage;
